import React from 'react'
import styled from '@emotion/styled'
import {css} from '@emotion/core'
import {mq} from '../../../../../utils/breakpoints'

import {hexToRGBStr} from '@daedalus/core/src/utils/string'
import {FormattedMessageWrapper} from '@daedalus/core/src/localization/components/FormattedMessage'

import {IsMemberPlusProp} from './types'
import {TierPerks} from './TierPerks'
import {useDeviceLayout} from '../../../../../context/deviceLayout'
import Card from '../../../general/Card'
import ContentWrapper from '../../../../designSystem/helpers/ContentWrapper'
import Heading from '../../../../designSystem/Heading'
import Text from '../../../../designSystem/Text'
import StepBar, {
  StepBarCustomMarker,
  StepBarLabel,
  StepBarMarker
} from '../../../privateDeals/StepBar'
import {PlusIcon} from '../../../privateDeals/PrivateDealTag/PlusIcon'

const MEMBER_TIER_POSITION = 30
const MEMBER_PLUS_POSITION = 100

const captionColor = isCurrentTier =>
  isCurrentTier ? 'midnight.m600' : 'midnight.m300'

const DescriptionWrapper = styled.div`
  ${css`
    display: flex;
    justify-content: center;
    gap: 2px;
  `}
`

const TierProgressionWrapper = styled.div(
  ({theme}) => css`
    padding: ${theme.layout.spacing.s500}px;
    margin-bottom: ${theme.layout.spacing.s500}px;
    background-image: linear-gradient(
      to bottom,
      ${theme.colors.privateDealColor},
      rgba(${hexToRGBStr(theme.colors.violet.v100)}, 0)
    );
    ${mq.desktopXs(css`
      padding: 0;
      background-image: none;
    `)}
  `
)

interface TierProgressionProps extends IsMemberPlusProp {}

export const TierProgression = ({isMemberPlus}: TierProgressionProps) => {
  const {isMobile} = useDeviceLayout()

  const memberCaptionColor = captionColor(!isMemberPlus)
  const memberPlusCaptionColor = captionColor(isMemberPlus)
  const tierBarPosition = isMemberPlus ? 100 : MEMBER_TIER_POSITION

  return (
    <TierProgressionWrapper>
      <Card>
        <ContentWrapper
          paddingSize={isMobile ? 's000' : 's900'}
          paddingTopSize={isMobile ? 's400' : 's700'}
          paddingBottomSize={isMobile ? 's000' : 's900'}
        >
          <Heading
            appearance={isMobile ? 'title2' : 'title1'}
            align="center"
            marginBottomSize="s000"
          >
            <FormattedMessageWrapper
              id="tierProgression.title"
              defaultMessage="Hi Traveler {emoji}"
              values={{emoji: '👋'}}
            />
          </Heading>

          <ContentWrapper paddingSize="s500">
            <DescriptionWrapper>
              <Text align="center" marginBottomSize="s600">
                <FormattedMessageWrapper
                  id="tierProgression.unlockedUnbeatableAccomodationDeals"
                  defaultMessage="You’ve unlocked unbeatable accomodation deals"
                />
              </Text>
            </DescriptionWrapper>

            <StepBar percentage={tierBarPosition}>
              <StepBarMarker isActive percentage={MEMBER_TIER_POSITION} />
              <StepBarLabel percentage={MEMBER_TIER_POSITION}>
                <Text
                  appearance="subtitle1"
                  align="center"
                  colorPath={memberCaptionColor}
                >
                  <FormattedMessageWrapper
                    id="tierProgression.member"
                    defaultMessage="Member"
                  />
                </Text>
              </StepBarLabel>

              <StepBarCustomMarker percentage={MEMBER_PLUS_POSITION}>
                <PlusIcon translateY="0" translateX="-50%" />
              </StepBarCustomMarker>
              <StepBarLabel
                centerPoint="right"
                percentage={MEMBER_PLUS_POSITION}
              >
                <Text
                  appearance="subtitle1"
                  align="right"
                  colorPath={memberPlusCaptionColor}
                >
                  <FormattedMessageWrapper
                    id="tierProgression.plusMember"
                    defaultMessage="Plus member"
                  />
                </Text>
              </StepBarLabel>
            </StepBar>
          </ContentWrapper>

          <TierPerks
            isMemberPlus={isMemberPlus}
            arrowPosition={isMobile ? tierBarPosition - 4 : tierBarPosition}
          />
        </ContentWrapper>
      </Card>
    </TierProgressionWrapper>
  )
}
